/** Angular Imports */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

/** Core Imports */
import { ApiGetAllResponse } from '../interfaces';

/** Local Imports */
import { ApiTimezone } from './timezone.interfaces';

/**
 * Service for fetching timezone data from the backend.
 *
 * @remarks
 * All requests go through the BFF proxy (`/api/...`) which forwards
 * them to the AWS backend. The result is cached via `shareReplay(1)`
 * since timezones rarely change during a session.
 *
 * @example
 * ```typescript
 * private readonly _timezoneService = inject(TimezoneService);
 *
 * this._timezoneService.getAll().subscribe(res => {
 *     console.log(res.data); // ApiTimezone[]
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class TimezoneService {
    /**
     * Angular HTTP client for API communication.
     */
    private readonly _http = inject(HttpClient);

    /**
     * Base URL for the timezones API endpoint.
     * Routed through the BFF proxy in `server.ts`.
     */
    private readonly _baseUrl = '/api/timezones';

    /**
     * Cached observable for timezone data.
     * Uses shareReplay(1) to avoid redundant API calls.
     */
    private _cachedTimezones$: Observable<ApiGetAllResponse<ApiTimezone>> | null = null;

    /**
     * Retrieves all timezones from the backend.
     * The result is cached for the lifetime of the service instance.
     *
     * @returns Observable of timezone list response
     */
    public getAll(): Observable<ApiGetAllResponse<ApiTimezone>> {
        if (!this._cachedTimezones$) {
            this._cachedTimezones$ = this._http
                .get<ApiGetAllResponse<ApiTimezone>>(this._baseUrl)
                .pipe(shareReplay({ bufferSize: 1, refCount: true }));
        }
        return this._cachedTimezones$;
    }
}
